/* Size picker — a display layer over the native size <select>.

   The <select> stays the single source of truth: global.js reads it to resolve
   the variant, Ecomid writes to it, and the sticky bar measures it. This element
   only mirrors it in both directions and swaps which pre-rendered label is
   visible. It never parses a size string — Liquid already did that. */

if (!customElements.get('size-picker')) {
  const SYSTEM_KEY = 'myrqvist:size-system';
  const UNIT_KEY = 'myrqvist:size-unit';
  const SYNC_EVENT = 'size-picker:system';

  const readStored = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      /* Safari private mode and storage-blocked contexts throw on access. A
         missing preference is not worth breaking the picker over. */
      return null;
    }
  };

  const writeStored = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Preference simply won't persist; the picker still works this session.
    }
  };

  class SizePicker extends HTMLElement {
    connectedCallback() {
      this.picker = this.querySelector('[data-size-picker]');
      this.select = this.querySelector('.product-form__input--dropdown select');
      if (!this.picker || !this.select) return;

      this.radios = Array.from(this.picker.querySelectorAll('input[type="radio"]'));
      this.status = this.picker.querySelector('[data-size-picker-status]');
      this.hint = this.picker.querySelector('[data-size-picker-hint]');
      this.routedHint = this.picker.querySelector('[data-size-picker-hint-routed]');
      this.altHint = this.picker.querySelector('[data-size-picker-hint-alt]');
      /* The same map <buy-box> reads, rendered once by the section, so a chip and
         the button it leads to can never disagree about a size. */
      this.variantStates = new Map(
        (this.picker.dataset.variantStates || '')
          .split(',')
          .filter(Boolean)
          .map((entry) => entry.split(':'))
      );
      this.systemButtons = Array.from(this.querySelectorAll('[data-size-system-option]'));
      this.storageKey = this.picker.dataset.sizeSystem === 'cm' || this.picker.dataset.sizeSystem === 'in' ? UNIT_KEY : SYSTEM_KEY;

      this.onRadioChange = this.handleRadioChange.bind(this);
      this.onSelectChange = this.handleSelectChange.bind(this);
      this.onSystemSync = this.handleSystemSync.bind(this);

      /* Listen on <variant-selects>, not on our own select: a change to ANOTHER
         option (colour, sole, buckle) never fires on this select, but it does
         change which sizes are purchasable. Our radios stopPropagation, so the
         only things reaching here are real select changes — ours and siblings'. */
      this.variantSelects = this.closest('variant-selects');
      this.changeHost = this.variantSelects || this.select;

      this.picker.addEventListener('change', this.onRadioChange);
      this.changeHost.addEventListener('change', this.onSelectChange);
      document.addEventListener(SYNC_EVENT, this.onSystemSync);

      /* The grid is now the control. Leaving the clipped select tabbable would
         give keyboard and screen-reader users a second, invisible copy of the
         same option. Done here rather than in Liquid so that a blocked script
         leaves the native select fully operable (see the :defined gate in CSS). */
      this.select.setAttribute('tabindex', '-1');
      const selectWrap = this.select.closest('.select');
      if (selectWrap) selectWrap.setAttribute('aria-hidden', 'true');

      this.systemButtons.forEach((button) => {
        button.addEventListener('click', () => this.applySystem(button.dataset.sizeSystemOption, true));
      });

      if (this.systemButtons.length) this.applyStoredSystem();
      this.syncAvailability();
    }

    disconnectedCallback() {
      /* quick-add empties its modal on close, so an un-removed document listener
         would leak, bound to a detached element, on every open. */
      document.removeEventListener(SYNC_EVENT, this.onSystemSync);
      if (this.picker) this.picker.removeEventListener('change', this.onRadioChange);
      if (this.changeHost) this.changeHost.removeEventListener('change', this.onSelectChange);
      if (this.select) {
        this.select.removeAttribute('tabindex');
        const wrap = this.select.closest('.select');
        if (wrap) wrap.removeAttribute('aria-hidden');
      }
    }

    applyStoredSystem() {
      const stored = readStored(this.storageKey);
      if (!stored) return;
      const known = this.systemButtons.some((button) => button.dataset.sizeSystemOption === stored);
      if (known) this.applySystem(stored, false);
    }

    applySystem(system, persist) {
      if (!system || this.picker.dataset.sizeSystem === system) return;
      this.picker.dataset.sizeSystem = system;
      this.systemButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.sizeSystemOption === system));
      });

      if (this.status) {
        /* Relabelling every chip under a focused radio is otherwise silent.
           Template comes from Liquid so it stays translatable. */
        const template = this.picker.dataset.statusTemplate;
        if (template) this.status.textContent = template.replace('SYSTEM', system.toUpperCase());
      }

      if (persist) {
        writeStored(this.storageKey, system);
        document.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { system, key: this.storageKey } }));
      }
    }

    handleSystemSync(event) {
      const { system, key } = event.detail || {};
      if (key !== this.storageKey) return;
      this.applySystem(system, false);
    }

    handleRadioChange(event) {
      const radio = event.target;
      if (!radio || radio.type !== 'radio') return;

      /* <variant-selects> listens for change on itself. Letting the radio's own
         event bubble would run a second, stale onVariantChange() alongside the
         one we dispatch on the select below. */
      event.stopPropagation();

      /* Chips and options are rendered from the same list, both consumers
         dropping the same values through size-hidden-values, so every radio
         value carries a matching <option>. */
      this.select.value = radio.value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.pushSizeSelected(radio);
    }

    /* VariantSelects.updateMasterId() (global.js) runs synchronously inside the
       change handler the dispatch in handleRadioChange triggers, so
       variantSelects.currentVariant is already the just-picked variant by the
       time we read it here — only the section re-fetch that follows
       (renderProductInfo()) is async. */
    pushSizeSelected(radio) {
      const variant = this.variantSelects.currentVariant;

      const chip = radio.closest('.size-picker__chip');
      const system = this.picker.dataset.sizeSystem;
      const labelEl =
        (system && chip.querySelector(`.size-picker__label[data-system="${system}"]`)) ||
        chip.querySelector('.size-picker__label');
      const size = labelEl ? labelEl.textContent.trim() : '';

      // Custom-named, no ecommerce object: GA4's view_item/select_item/add_to_cart
      // already fire from the checkout_datalayer Web Pixel, so reusing one of
      // those names here would double-count it.
      let variantId = '';
      let state;
      if (variant) {
        variantId = String(variant.id);
        state = this.variantStates.get(variantId) || 'unknown';
      } else {
        /* No currentVariant means the picked combination has no entry in the
           section's visible variant JSON: either variant-json-visible.liquid
           dropped it (size_hidden and unavailable) or the size/option pair never
           existed. Both land the shopper on sold out, and both are the lost pick
           this event exists to count, so report it instead of dropping it. */
        state = 'soldout';
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pdp_size_selected',
        product_id: this.picker.dataset.productId,
        variant_id: variantId,
        size,
        size_state: state,
        buyable: state === 'available' || state === 'preorder',
      });
    }

    handleSelectChange() {
      /* Ecomid writes straight to the select, so the grid follows it. */
      this.syncFromSelect();
      this.syncAvailability();
    }

    syncFromSelect() {
      const current = this.select.value;
      this.radios.forEach((radio) => {
        radio.checked = radio.value === current;
      });
    }

    getVariantData() {
      if (this.variantData) return this.variantData;
      const holder = this.closest('variant-selects') || this;
      const json = holder.querySelector('script[type="application/json"]');
      try {
        this.variantData = json ? JSON.parse(json.textContent) : [];
      } catch (error) {
        console.error('size-picker: could not read variant data', error);
        this.variantData = [];
      }
      return this.variantData;
    }

    syncAvailability() {
      const variants = this.getVariantData();
      if (!variants.length) return;

      const selects = Array.from(this.closest('variant-selects')?.querySelectorAll('select') || []);
      const index = selects.indexOf(this.select);
      if (index === -1) return;

      const others = selects.map((select) => select.value);
      const available = new Set();
      /* Read from the map, not from the JSON's `available`: variant-json-visible
         deliberately flips restricted variants to unavailable so every other JS
         reader treats them as sold out, which loses exactly the distinction the
         bell depends on — shown where a restock alert exists, hidden where
         <buy-box> will refuse one. Same split as the Liquid render, so a change
         to another option keeps the chips honest. */
      const restricted = new Set();
      const alternate = new Set();
      variants.forEach((variant) => {
        const matches = variant.options.every((option, i) => i === index || others[i] === option);
        if (!matches) return;
        const state = this.variantStates.get(String(variant.id));
        // Pre-order is a live choice, so it counts as available here.
        if (state === 'available' || state === 'preorder') available.add(variant.options[index]);
        else if (state === 'alternate') alternate.add(variant.options[index]);
        else if (state === 'restricted') restricted.add(variant.options[index]);
      });

      let soldOutCount = 0;
      let alternateCount = 0;
      this.radios.forEach((radio) => {
        const chip = radio.closest('.size-picker__chip');
        if (!chip) return;
        const soldOut = !available.has(radio.value);
        /* Alternate outranks restricted: a size the other generation can ship
           has somewhere to go, and that beats being told the stock exists at a
           store that cannot reach this shopper. */
        const isAlternate = soldOut && alternate.has(radio.value);
        const isRestricted = soldOut && !isAlternate && restricted.has(radio.value);
        if (isAlternate) alternateCount += 1;
        else if (soldOut && !isRestricted) soldOutCount += 1;
        // Same rule as the Liquid render: a routed size reads as in stock.
        chip.classList.toggle('size-picker__chip--soldout', soldOut && !isAlternate);
        chip.classList.toggle('size-picker__chip--alternate', isAlternate);
        chip.classList.toggle('size-picker__chip--restricted', isRestricted);
        // Deliberately no aria-disabled: a sold-out chip is operable, and it is the
        // only route to a restock alert. The visually-hidden note carries the status.
        // An alternate chip swaps that note for one naming where the size went, so
        // the two are toggled against each other and never read together.
        const note = chip.querySelector('[data-soldout-note]');
        if (note) note.hidden = !soldOut || isAlternate;
        const altNote = chip.querySelector('[data-alternate-note]');
        if (altNote) altNote.hidden = !isAlternate;
      });

      this.syncHint(soldOutCount, alternateCount);
    }

    // A restricted size counts in neither total, so its presence stops the two
    // summing to radios.length and all three hints stay hidden — matches Liquid.
    syncHint(soldOutCount, alternateCount) {
      const deadEnd = soldOutCount + alternateCount === this.radios.length;
      if (this.hint) this.hint.hidden = !deadEnd || alternateCount > 0;
      if (this.routedHint) this.routedHint.hidden = !deadEnd || alternateCount === 0 || soldOutCount > 0;
      if (this.altHint) this.altHint.hidden = !deadEnd || alternateCount === 0 || soldOutCount === 0;
    }
  }

  customElements.define('size-picker', SizePicker);
}
